/** IdeasPage — same shell as Decisions/Tasks. Reads public.ideas. */
import { useEffect, useMemo, useState } from "react";
import { Lightbulb, Loader2, Filter } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import { supabase } from "@/lib/supabaseClient";
import { useActiveProject } from "@/context/ActiveProjectContext";

export default function IdeasPage() {
    const { projects } = useActiveProject();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [projectFilter, setProjectFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        supabase
            .from("ideas")
            .select("id, project_id, idea_text, status, source_type, source_session_id, promoted_to_decision_id, created_at")
            .order("created_at", { ascending: false })
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) setError(error.message);
                else setRows(data ?? []);
            });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => (rows || []).filter((r) => projectFilter === "all" || r.project_id === projectFilter), [rows, projectFilter]);

    return (
        <div data-testid="ideas-page" className="space-y-6">
            <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Ideas</h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    Half-formed sparks, thoughts-in-motion, and things you want to come back to.
                </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 h-9 px-3 rounded-full qoryn-panel font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                    <Filter className="h-3 w-3" strokeWidth={1.75} />
                    Filter
                </div>
                <select
                    data-testid="ideas-project-filter"
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    className="h-9 px-3 rounded-full qoryn-panel bg-transparent font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-text qoryn-focus"
                >
                    <option value="all">All projects</option>
                    {projects.filter((p) => p.status !== "archived").map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
            </div>
            {rows === null && !error && (
                <div className="py-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
            )}
            {error && (
                <EmptyState icon={Lightbulb} title="Couldn't load ideas." body={error} />
            )}
            {rows !== null && !error && filtered.length === 0 && (
                <EmptyState
                    icon={Lightbulb}
                    title="No ideas yet."
                    body="Ideas land here after a voice session is summarized and promoted, or when you record one manually."
                />
            )}
            {filtered.length > 0 && (
                <ul data-testid="ideas-list" className="space-y-2">
                    {filtered.map((i) => (
                        <li key={i.id} data-testid={`idea-row-${i.id}`} className="qoryn-panel rounded-2xl px-5 py-4 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                                <p className="font-sans text-[14px] text-qoryn-text leading-relaxed flex-1">
                                    {i.idea_text}
                                </p>
                                <span className="inline-flex rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-cyan bg-qoryn-cyan/10">
                                    {i.status}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <ProvenanceBadge sourceType={i.source_type} sourceSessionId={i.source_session_id} />
                                {i.promoted_to_decision_id && (
                                    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono-ui text-[8px] tracking-widest uppercase text-emerald-300 bg-emerald-500/10">
                                        promoted → decision
                                    </span>
                                )}
                                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                                    {new Date(i.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
